import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { type RunsFilters, RunsPage } from "@/features/runs/RunsPage";
import { runsQuery } from "@/lib/api/queries";
import { DEFAULT_ENV, projectSearch } from "@/lib/search";

const search = projectSearch.extend({
  status: z.enum(["all", "failed", "running", "succeeded", "warning", "cancelled"]).optional(),
  job: z.string().optional(),
  trigger: z.enum(["all", "scheduled", "manual", "ci"]).optional(),
  range: z.enum(["24h", "7d", "30d", "90d"]).optional(),
  q: z.string().optional(),
  page: z.number().int().min(1).optional(),
});

const defaults: RunsFilters = {
  status: "all",
  job: "all",
  trigger: "all",
  range: "7d",
  q: "",
  page: 1,
};

export const Route = createFileRoute("/w/$workspace/p/$project/runs/")({
  validateSearch: search,
  loaderDeps: ({ search: s }) => ({ ...defaults, ...strip(s), env: s.env ?? DEFAULT_ENV }),
  loader: ({ context, params, deps }) =>
    context.queryClient.ensureQueryData(
      runsQuery(params.workspace, params.project, { ...deps, pageSize: 12 }),
    ),
  component: RunsRoute,
});

function strip<T extends object>(o: T): Partial<T> {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as Partial<T>;
}

function RunsRoute() {
  const { workspace, project } = Route.useParams();
  const s = Route.useSearch();
  const navigate = Route.useNavigate();
  const filters: RunsFilters = { ...defaults, ...strip(s) };
  return (
    <RunsPage
      workspace={workspace}
      project={project}
      env={s.env ?? DEFAULT_ENV}
      filters={filters}
      onFilters={(patch) =>
        navigate({
          search: (prev) => {
            const next = { ...prev, ...patch } as Record<string, unknown>;
            // Keep the URL clean: drop values equal to the defaults.
            for (const [k, v] of Object.entries(defaults)) if (next[k] === v) delete next[k];
            return next as typeof prev;
          },
          replace: true,
        })
      }
    />
  );
}
